import { useEffect, useState } from "react";
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';


const EditPriceModal = ({ show, onHide, priceData, onUpdate, securityName }) => {
  const [formData, setFormData] = useState(priceData || {});
  const [confirmAdjustedPrice, setConfirmAdjustedPrice] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Réinitialiser le formulaire quand les données changent
  useEffect(() => {
    if (priceData) {
      // Normaliser les données: s'assurer que 'price' existe
      const normalizedData = {
        ...priceData,
        price: priceData.currentPrice || priceData.price || ''
      };
      setFormData(normalizedData);
      setConfirmAdjustedPrice(null);
      setFormErrors({});
    }
  }, [priceData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Effacer l'erreur du champ modifié
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleConfirmChange = (e) => {
    const value = e.target.value;
    setConfirmAdjustedPrice(value);
    
    setFormErrors(prev => ({ 
      ...prev, 
      confirmAdjustedPrice: value !== formData.adjustedPrice 
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.adjustedPrice) {
      errors.adjustedPrice = "Le nouveau prix est obligatoire";
    }
    
    if (formData.adjustedPrice !== confirmAdjustedPrice) {
      errors.confirmAdjustedPrice = "Les prix ne correspondent pas";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Préparer les données pour l'API
      const submissionData = {
        securityId: priceData?.securityId,
        adjustedPrice: formData.adjustedPrice,
        volume: formData.volume || null,
        currentPrice: formData.price // Inclure le prix actuel pour référence
      };
      
      // console.log('Données soumises:', submissionData);
      
      const success = await onUpdate(submissionData, securityName);
      if (success) {
        onHide();
      }
    } catch (error) {
      // console.error("Erreur soumission prix:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Modifier le prix du titre</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          Modification du prix pour : <strong>{securityName}</strong>
        </div>

        <form>
          {/* Prix actuel (lecture seule) */}
          <div className="mb-3">
            <label htmlFor="price" className="form-label fw-semibold">
              Prix actuel
            </label>
            <input
              type="number"
              className="form-control bg-light"
              id="price"
              name="price"
              value={formData.price || ''}
              readOnly
            />
            <div className="form-text text-muted">
              Prix actuel du titre (non modifiable)
            </div>
          </div>

          {/* Nouveau prix */}
          <div className="mb-3">
            <label htmlFor="adjustedPrice" className="form-label fw-semibold">
              Nouveau prix <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              className={`form-control ${formErrors.adjustedPrice ? 'is-invalid' : ''}`}
              id="adjustedPrice"
              name="adjustedPrice"
              value={formData.adjustedPrice || ''}
              onChange={handleChange}
              required
              step="0.01"
              min="0"
              placeholder="Saisissez le nouveau prix"
            />
            {formErrors.adjustedPrice && (
              <div className="invalid-feedback">{formErrors.adjustedPrice}</div>
            )}
            <div className="form-text text-muted">
              Saisissez le nouveau prix du titre
            </div>
          </div>

          {/* Confirmation nouveau prix */}
          {formData.adjustedPrice && (
            <div className="mb-3">
              <label htmlFor="confirmAdjustedPrice" className="form-label fw-semibold">
                Confirmer le nouveau prix <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={`form-control ${formErrors.confirmAdjustedPrice ? 'is-invalid' : ''}`}
                id="confirmAdjustedPrice"
                name="confirmAdjustedPrice"
                value={confirmAdjustedPrice || ''}
                onChange={handleConfirmChange}
                required
                step="0.01"
                min="0"
                placeholder="Resaisissez le même prix"
              />
              {formErrors.confirmAdjustedPrice && (
                <div className="invalid-feedback">{formErrors.confirmAdjustedPrice}</div>
              )}
              <div className="form-text text-muted">
                Resaisissez le nouveau prix pour confirmation
              </div>
            </div>
          )}

          {/* Volume (optionnel) */}
          <div className="mb-3">
            <label htmlFor="volume" className="form-label fw-semibold">
              Volume
            </label>
            <input
              type="number"
              className="form-control"
              id="volume"
              name="volume"
              value={formData.volume || ''}
              onChange={handleChange}
              min="0"
              placeholder="Optionnel"
            />
            <div className="form-text text-muted">
              Volume associé à la modification (optionnel)
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button 
          className="btn btn-secondary" 
          onClick={onHide}
          disabled={loading}
        >
          Annuler
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Mise à jour...
            </>
          ) : (
            'Mettre à jour'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

EditPriceModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  priceData: PropTypes.shape({
    securityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currentPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Ajoutez ceci
    adjustedPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    volume: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  onUpdate: PropTypes.func.isRequired,
  securityName: PropTypes.string.isRequired
};

export default EditPriceModal;